import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ListIcon } from '@phosphor-icons/react';
import { Logo } from '@growhero/brand';
import { IconButton, Sheet } from '@/ui';
import { portalNav, adminNav, type NavGroup } from '../nav';
import { SidebarContent } from './Sidebar';
import { TrialBanner } from './TrialBanner';
import { useGlobalApiErrors } from './useGlobalApiErrors';

function Shell({ groups, variant }: { groups: NavGroup[]; variant: 'portal' | 'admin' }) {
  const { t } = useTranslation('nav');
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useGlobalApiErrors();

  // Move focus to main content on navigation for screen readers, and scroll to top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [loc.pathname]);

  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[16.5rem_1fr]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-sm focus:bg-ink focus:px-3 focus:py-2 focus:text-ink-inverse"
      >
        {t('skipToContent')}
      </a>
      {/* A3: the sidebar is the brand field (deep sea blue); kit components adapt via surface-field. */}
      <aside className="surface-field sticky top-0 hidden h-dvh lg:block" data-surface="field">
        <SidebarContent groups={groups} variant={variant} />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-hairline bg-canvas/95 px-3 backdrop-blur-sm lg:hidden">
        <Sheet
          open={open}
          onOpenChange={setOpen}
          side="left"
          bare
          title={t('mainNav')}
          width="w-[min(18rem,calc(100vw-3rem))]"
          trigger={
            <IconButton label={t('openMenu')}>
              <ListIcon />
            </IconButton>
          }
        >
          <div className="surface-field h-full" data-surface="field">
            <SidebarContent groups={groups} variant={variant} onNavigate={() => setOpen(false)} />
          </div>
        </Sheet>
        <Link to={variant === 'admin' ? '/platform-admin' : '/'} aria-label="GrowHero">
          <Logo height={22} title="GrowHero" />
        </Link>
        <span className="w-9.5" />
      </header>

      <main id="main" tabIndex={-1} className="min-w-0 focus:outline-none">
        <div className="mx-auto w-full max-w-[76rem] px-4 pb-20 pt-7 sm:px-6 lg:px-10 lg:pt-9">
          {variant === 'portal' && <TrialBanner />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function AppShell() {
  return <Shell groups={portalNav} variant="portal" />;
}

export function AdminShell() {
  return <Shell groups={adminNav} variant="admin" />;
}
